import translationService from '@/services/translationService';

/**
 * Translation mixin that provides translation functionality to Vue components
 * Makes the $t method available to all components that use this mixin
 */
export const translationMixin = {
  data() {
    return {
      currentLanguage: translationService.getCurrentLanguage(),
      isTranslationLoading: false
    };
  },

  methods: {
    /**
     * Translate a key to the current language
     * @param {string} key - Translation key (e.g., 'nav.home')
     * @param {Object} params - Optional parameters for string interpolation
     * @returns {string} Translated text
     */
    $t(key, params = {}) {
      return translationService.translate(key, params);
    },

    /**
     * Change the current language
     * @param {string} language - Language code to switch to
     * @returns {Promise<boolean>} Promise that resolves when language is changed
     */
    async $setLanguage(language) {
      this.isTranslationLoading = true;
      
      try {
        const success = await translationService.setLanguage(language);
        
        if (success) {
          this.currentLanguage = language;
          // Force reactivity update for all components
          this.$forceUpdate();
          
          // Emit language change event for other components to listen to
          this.$root.$emit('languageChanged', language);
        }
        
        return success;
      } catch (error) {
        console.error('Error changing language:', error);
        return false;
      } finally {
        this.isTranslationLoading = false;
      }
    },

    /**
     * Get the current language code
     * @returns {string} Current language code
     */
    $getCurrentLanguage() {
      return translationService.getCurrentLanguage();
    },

    /**
     * Get all supported languages
     * @returns {Object} Supported languages configuration
     */
    $getSupportedLanguages() {
      return translationService.getSupportedLanguages();
    },

    /**
     * Check if a language is loaded
     * @param {string} language - Language code to check
     * @returns {boolean} True if language is loaded
     */
    $isLanguageLoaded(language) {
      return translationService.isLanguageLoaded(language);
    }
  },

  async created() {
    // Wait for translation service to initialize
    await translationService.waitForInitialization();
    
    // Update current language and force re-render once initialized
    this.currentLanguage = translationService.getCurrentLanguage();
    this.$forceUpdate();
    
    // Listen for language changes from other components
    this.$root.$on('languageChanged', (newLanguage) => {
      if (this.currentLanguage !== newLanguage) {
        this.currentLanguage = newLanguage;
        this.$forceUpdate();
      }
    });
  },

  beforeDestroy() {
    // Clean up event listeners
    this.$root.$off('languageChanged');
  }
};

export default translationMixin;